import { Fragment } from 'react';
import { C, SectionLabel, H2, wrap, useIsMobile } from '../styles';
import Photo from './Photo';
import { PAIR_DRAWING_SLUG, PAIR_PART_SLUG } from '../content/photos';

const steps = [
  { n: '01', t: 'Send us your part', b: 'Upload a photo, scan, or hand sketch of your part. No CAD files required — a rough drawing with approximate dimensions is enough to begin.' },
  { n: '02', t: 'We build your brief', b: 'Our team turns your drawing into a structured brief with concept visuals, using AI-assisted drawing tools to move fast. We confirm the specifications with you before anything reaches a shop floor.' },
  { n: '03', t: 'You confirm the specs', b: 'You receive concept renderings and a written brief. Approve as-is or request changes. Nothing moves forward without your sign-off.' },
  { n: '04', t: 'Get your accurate quote', b: 'We put your confirmed brief to our network of 20+ Ontario fabrication shops and come back with an accurate quote — in under 72 hours.' },
];

// Task 5 — the drawing/part pairing, and the single most misuse-prone thing on the site.
//
// THE DRAWING AND THE WELDED COLUMN BASES ARE TWO UNRELATED JOBS. Ian confirmed it.
// They look like a matched pair, which is exactly the trap.
//
// Both statements below are true of the *service*. Neither claims this drawing produced
// that part, and the layout is two distinct cards precisely so nothing implies it did.
//
// DO NOT, in copy, captions, alt text or og: tags, write "same job", "this drawing
// became", "from sketch to this part", or any construction implying one produced the
// other. DO NOT replace this with a before/after slider, a dissolve, an arrow between
// the two cards, or anything else that reads as a transformation of one into the other.
// The alt text in the manifest is already written to be safe — it is used verbatim.
//
// A genuine matched pair is worth getting properly: the plan is to ask Ian to photograph
// the next job he takes from drawing to finished part. A real one is worth more than a
// convincing one. Until that exists, this stays as two separate cards.
const pair = [
  { slug: PAIR_DRAWING_SLUG, k: 'You send this.', sub: 'A drawing, a sketch, or a photo with dimensions on it.' },
  { slug: PAIR_PART_SLUG, k: 'You get this.', sub: 'Fabricated, welded, and finished by a shop in the network.' },
];

function DrawingAndPart({ isMobile }) {
  return (
    <div id="drawing-and-part" style={{
      background: C.surf, border: `1px solid ${C.bdr}`, borderRadius: 10,
      padding: isMobile ? '20px 18px' : '30px',
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 22 : 26, alignItems: 'start' }}>
        {pair.map(({ slug, k, sub }) => (
          <div key={slug}>
            <h4 style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 22, fontWeight: 700, textTransform: 'uppercase', color: C.txt, marginBottom: 4 }}>{k}</h4>
            <p style={{ color: C.mut, fontSize: 13, lineHeight: 1.6, marginBottom: 14 }}>{sub}</p>
            <Photo
              slug={slug}
              sizes={'(max-width: 768px) 88vw, 496px'}
              style={{ borderRadius: 8, overflow: 'hidden', border: `1px solid ${C.bdr}` }}
            />
          </div>
        ))}
      </div>
      {/* Says out loud what the layout already implies, because the two images look
          more related than they are. This sentence is what keeps the pairing honest. */}
      <p style={{ color: C.dim, fontSize: 12, lineHeight: 1.6, marginTop: 18 }}>
        Two separate jobs, both from our partner's shop — an example of the drawings we work from,
        and an example of the work that comes back.
      </p>
    </div>
  );
}

export default function HowItWorks() {
  const isMobile = useIsMobile();
  return (
    <section id="how" style={{ padding: isMobile ? '64px 0' : '88px 0', borderTop: `1px solid ${C.bdr}` }}>
      <div style={wrap}>
        <SectionLabel>Process</SectionLabel>
        <H2 style={{ maxWidth: 500, marginBottom: isMobile ? 32 : 44 }}>From a photo<br />to an accurate quote</H2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {steps.map((s) => (
            <Fragment key={s.n}>
              <div style={{
                background: C.surf, border: `1px solid ${C.bdr}`, borderRadius: 10,
                display: 'grid', gridTemplateColumns: isMobile ? '52px 1fr' : '90px 1fr', gap: isMobile ? 14 : 20,
                alignItems: 'center', padding: isMobile ? '20px 18px' : '26px 30px', transition: 'border-color 0.2s',
              }}
                onMouseEnter={e => e.currentTarget.style.borderColor = C.navy}
                onMouseLeave={e => e.currentTarget.style.borderColor = C.bdr}
              >
                <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: isMobile ? 48 : 78, fontWeight: 700, color: C.bdr, lineHeight: 1, userSelect: 'none' }}>{s.n}</div>
                <div>
                  <h3 style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 23, fontWeight: 700, textTransform: 'uppercase', color: C.txt, marginBottom: 8 }}>{s.t}</h3>
                  <p style={{ color: C.mut, fontSize: 14, lineHeight: 1.7 }}>{s.b}</p>
                </div>
              </div>
              {s.n === '01' && <DrawingAndPart isMobile={isMobile} />}
            </Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}
